import { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { ADDRESS_PREFIX, VECNO_UNIT } from '../explorer_constants';
import { getAddressBalance } from '../vecno-api-client';
import LastBlocksContext from './LastBlocksContext';
import { numberWithCommas } from '../helper';

const WalletTracker = () => {
  const [walletAddress, setWalletAddress] = useState(() => {
    return localStorage.getItem('walletAddress')?.trim() || '';
  });
  const [walletBalance, setWalletBalance] = useState(() => {
    const savedBalance = localStorage.getItem('walletBalance');
    return savedBalance ? parseFloat(savedBalance) : null;
  });
  const [error, setError] = useState('');
  const { isConnected } = useContext(LastBlocksContext);
  const hasInitialized = useRef(false);
  const isFetching = useRef(false);

  const fetchWalletBalance = useCallback(async (address) => {
    if (isFetching.current) {
      console.log('Fetch skipped: Already fetching');
      return;
    }
    isFetching.current = true;
    console.log('Initiating fetch for address:', address);
    try {
      const balance = await getAddressBalance(address);
      const newBalance = balance / 100000000;
      console.log('Balance fetched:', newBalance);
      setWalletBalance(newBalance);
      localStorage.setItem('walletBalance', newBalance.toString());
      return newBalance;
    } catch (err) {
      console.warn('Fetch error:', err.message);
      setError('Failed to fetch balance');
      throw new Error('Invalid address or API error');
    } finally {
      isFetching.current = false;
    }
  }, []);

  const handleRefresh = useCallback(() => {
    if (walletAddress) {
      fetchWalletBalance(walletAddress);
    }
  }, [fetchWalletBalance, walletAddress]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const address = e.target.walletInput.value.trim().toLowerCase();
    const prefix = ADDRESS_PREFIX.endsWith(':') ? ADDRESS_PREFIX : `${ADDRESS_PREFIX}:`;
    if (!address.startsWith(prefix.toLowerCase()) || address.length < prefix.length + 12) {
      setError(`Address must begin with "${prefix}" and be a valid address`);
      setWalletBalance(null);
      localStorage.removeItem('walletBalance');
      return;
    }
    try {
      const balance = await fetchWalletBalance(address);
      setWalletAddress(address);
      setError('');
      localStorage.setItem('walletAddress', address);
      localStorage.setItem('walletBalance', balance.toString());
      e.target.walletInput.value = '';
    } catch (err) {
      setError(err.message);
      setWalletBalance(null);
      localStorage.removeItem('walletBalance');
    }
  };

  const handleClear = () => {
    setWalletAddress('');
    setWalletBalance(null);
    setError('');
    localStorage.removeItem('walletAddress');
    localStorage.removeItem('walletBalance');
    isFetching.current = false;
  };

  const shortenHash = (hash) => {
    if (!hash) {
      console.warn('shortenHash: Empty hash provided');
      return '';
    }

    const prefix = ADDRESS_PREFIX.endsWith(':') ? ADDRESS_PREFIX : `${ADDRESS_PREFIX}:`;
    const normalizedHash = hash.trim().toLowerCase();
    console.log('shortenHash: Input hash:', normalizedHash);
    console.log('shortenHash: Expected prefix:', prefix);

    if (!normalizedHash.startsWith(prefix.toLowerCase())) {
      console.warn('shortenHash: Prefix mismatch, returning unchanged:', normalizedHash);
      return normalizedHash;
    }

    const addressPart = normalizedHash.slice(prefix.length);
    console.log('shortenHash: addressPart:', addressPart, 'length:', addressPart.length);

    if (addressPart.length < 12) {
      console.warn('shortenHash: Address too short, returning unchanged:', normalizedHash);
      return normalizedHash;
    }

    const shortened = `${prefix}${addressPart.slice(0, 6)}...${addressPart.slice(-6)}`;
    console.log('shortenHash: Shortened result:', shortened);
    return shortened;
  };

  useEffect(() => {
    if (walletAddress && isConnected && !hasInitialized.current) {
      hasInitialized.current = true;
      console.log('Initial fetch for', walletAddress);
      fetchWalletBalance(walletAddress)
        .then((balance) => {
          setWalletBalance(balance);
          localStorage.setItem('walletBalance', balance.toString());
        })
        .catch(() => {
          setError('Failed to load balance on initialization');
          setWalletBalance(null);
          localStorage.removeItem('walletBalance');
        });
    }
  }, [walletAddress, isConnected, fetchWalletBalance]);

  return (
    <div className="wallet-tracker d-none d-md-flex align-items-center mx-3">
      {!(walletAddress && walletBalance !== null) && (
        <Form onSubmit={handleSubmit} className="d-flex">
          <InputGroup size="sm">
            <Form.Control
              name="walletInput"
              placeholder="Track wallet address"
              className="bg-dark text-light border-dark"
              style={{ fontSize: '0.8rem', maxWidth: '150px' }}
              isInvalid={!!error}
            />
            <InputGroup.Text className="bg-dark border-dark p-0">
              <button type="submit" className="btn btn-sm btn-dark p-1" aria-label="Track wallet balance">
                <i className="fa fa-search" style={{ color: '#14F1D9' }} />
              </button>
            </InputGroup.Text>
            {error && (
              <Form.Control.Feedback type="invalid" tooltip>
                {error}
              </Form.Control.Feedback>
            )}
          </InputGroup>
        </Form>
      )}
      {walletAddress && walletBalance !== null && (
        <>
          <span className="text-light ms-2" style={{ fontSize: '0.8rem' }}>
            {shortenHash(walletAddress)}
          </span>
          <span className="badge bg-dark text-light ms-2" style={{ fontSize: '0.8rem', border: '1px solid #14F1D9' }}>
            {numberWithCommas(parseFloat(walletBalance.toFixed(1)))} {VECNO_UNIT}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-dark ms-2 p-1"
            onClick={handleRefresh}
            aria-label="Refresh wallet balance"
            disabled={isFetching.current}
          >
            <i className="fa fa-refresh" style={{ color: '#14F1D9' }} />
          </button>
          <button
            type="button"
            className="btn btn-sm btn-dark ms-2 p-1"
            onClick={handleClear}
            aria-label="Clear wallet tracking"
          >
            <i className="fa fa-times" style={{ color: '#14F1D9' }} />
          </button>
        </>
      )}
    </div>
  );
};

export default WalletTracker;